import debounce from 'lodash.debounce';
import uniqueId from 'lodash.uniqueid';
import { Children, Component } from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

var includes = (function (arr, searchElement) {
  if (!Array.isArray(arr)) {
    return false;
  }

  return arr.indexOf(searchElement) !== -1;
});

var childMatchesType = (function (child, type, checkDisplayName) {
  if (checkDisplayName === void 0) {
    checkDisplayName = false;
  }

  var matches = false;

  if (!child || !child.type) {
    return matches;
  }

  matches = Array.isArray(type) ? type.includes(child.type) : child.type === type;

  if (checkDisplayName && !matches) {
    var name = child.type.displayName || child.type.name;
    matches = type.displayName === name;
  }

  return matches;
});

var filterProps = (function (props, filterList) {
  return Object.keys(props).reduce(function (newProps, prop) {
    var _Object$assign;

    if (includes(filterList, prop)) {
      return newProps;
    }

    return Object.assign({}, newProps, (_Object$assign = {}, _Object$assign[prop] = props[prop], _Object$assign));
  }, {});
});

var getBestKey = (function (props, fallback) {
  if (props === void 0) {
    props = {};
  }

  if (!Object.keys(props).length) {
    return fallback;
  }

  return props.id || props.href || props.to || props.name || fallback;
});

var getComponentName = (function (component) {
  if (!component) return null;
  var type = component.type || component;

  if (typeof type === 'string') {
    return type;
  }

  if (typeof type === 'function') {
    return type.displayName || type.name;
  }

  return null;
});

var getUniqueId = (function (prefix) {
  return uniqueId(prefix);
});

var onKeyPress = (function (match, callback) {
  return function (e) {
    if (match.filter(function (key) {
      return e.key && e.key === key.name || e.keyCode && e.keyCode === key.code;
    }).length > 0) {
      callback(e);
    }
  };
});

var setHasOverlay = (function (visible, handler) {
  var className = 'has-overlay';

  if (!handler && typeof document === 'undefined') {
    return false;
  }

  var classHandler = handler || document.body.classList;

  if (visible) {
    return classHandler.add(className);
  }

  return classHandler.remove(className);
});

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

var ClickAway = /*#__PURE__*/function (_Component) {
  _inheritsLoose(ClickAway, _Component);

  function ClickAway(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.mounted = false;
    _this.selfNode = null;
    _this.handleClickAway = _this.handleClickAway.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = ClickAway.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.selfNode = ReactDOM.findDOMNode(this); // eslint-disable-line react/no-find-dom-node

    this.mounted = true;
    document.addEventListener('click', this.handleClickAway);
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    this.mounted = false;
    document.removeEventListener('click', this.handleClickAway);
  };

  _proto.handleClickAway = function handleClickAway(event) {
    var mounted = this.mounted,
        selfNode = this.selfNode;
    var _this$props = this.props,
        inactive = _this$props.inactive,
        onClickAway = _this$props.onClickAway;

    if (inactive || !mounted || !selfNode) {
      return;
    }

    if (document.documentElement && document.documentElement.contains(event.target) && !selfNode.contains(event.target)) {
      onClickAway(event);
    }
  };

  _proto.render = function render() {
    var children = this.props.children;
    return Children.only(children);
  };

  return ClickAway;
}(Component);

ClickAway.propTypes = {
  onClickAway: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  inactive: PropTypes.bool
};
ClickAway.defaultProps = {
  inactive: false
};
ClickAway.displayName = 'ClickAway';

var Portal = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Portal, _Component);

  function Portal(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    var exclusive = props.exclusive,
        selector = props.selector;
    _this.state = {
      mounted: false
    };

    if (typeof document !== 'undefined' && document.createElement) {
      _this.parentEl = selector ? document.querySelector(selector) || document.body : document.body;
      _this.el = document.createElement('div');

      if (exclusive) {
        _this.id = props.id || getUniqueId('portal-');
        _this.el.id = _this.id;
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('Using Portal without document present. Children will not be rendered.');
    }

    return _this;
  }

  var _proto = Portal.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var _this$props = this.props,
        prepend = _this$props.prepend,
        exclusive = _this$props.exclusive;

    if (typeof document === 'undefined' || exclusive && document.getElementById(this.id)) {
      this.el = null;
      return;
    }

    if (this.parentEl) {
      if (prepend) {
        this.parentEl.insertBefore(this.el, this.parentEl.firstChild);
      } else {
        this.parentEl.appendChild(this.el);
      }

      this.setState({
        mounted: true
      });
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (document && this.parentEl && this.el) {
      this.parentEl.removeChild(this.el);
    }
  };

  _proto.render = function render() {
    var children = this.props.children;
    var mounted = this.state.mounted;
    return mounted && this.el && createPortal(children, this.el);
  };

  return Portal;
}(Component);

Portal.propTypes = {
  children: PropTypes.node,
  prepend: PropTypes.bool,
  exclusive: PropTypes.bool,
  id: PropTypes.string,
  selector: PropTypes.string
};
Portal.defaultProps = {
  children: undefined,
  prepend: false,
  exclusive: false,
  id: undefined,
  selector: undefined
};
Portal.displayName = 'Portal';

export { includes as arrayIncludes, childMatchesType, debounce, filterProps, getBestKey, getComponentName, getUniqueId, onKeyPress, setHasOverlay, ClickAway, Portal };
//# sourceMappingURL=index.js.map



//////////////////
// WEBPACK FOOTER
// ./~/@jsluna/utils/dist/index.js
// module id = 38
// module chunks = 0